/**
 * Communication Tour Configuration
 * Introduces users to call, SMS, email, and voicemail history features
 */

import { TourDefinition } from './tour.types';

export const communicationTour: TourDefinition = {
  id: 'communication-tour',
  name: 'Communication History',
  page: '/communication',
  version: 4, // Enhanced descriptions for clarity
  nextTourId: 'fax-tour', // Chain to fax tour
  steps: [
    {
      id: 'comm-intro',
      targetSelector: '[data-tour="communication-header"]',
      title: 'Communication Center',
      description:
        'Your central hub for all communications with leads and clients. Track calls, texts, emails, and voicemails - everything in one organized place.',
      placement: 'bottom',
    },
    {
      id: 'comm-tabs',
      targetSelector: '[data-tour="communication-tabs"]',
      title: 'Communication Channels',
      description:
        'Switch between different communication channels using these tabs. Each channel shows detailed history with timestamps, status, and quick actions.',
      placement: 'bottom',
    },
    {
      id: 'calls-tab',
      targetSelector: '[data-tour="communication-tab-calls"]',
      title: 'Call History',
      description:
        'View all your incoming and outgoing calls. See call duration, outcome status, and listen to call recordings when available.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="communication-tab-calls"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'sms-tab',
      targetSelector: '[data-tour="communication-tab-sms"]',
      title: 'SMS Messages',
      description:
        'Track all text messages sent and received. View delivery status, read receipts, and full conversation threads at a glance.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="communication-tab-sms"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'email-tab',
      targetSelector: '[data-tour="communication-tab-emails"]',
      title: 'Email History',
      description:
        'See all emails sent to your leads. Track opens, clicks, and delivery status to understand engagement with your email outreach.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="communication-tab-emails"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'voicemail-tab',
      targetSelector: '[data-tour="communication-tab-voicemail"]',
      title: 'Voicemail Inbox',
      description:
        'Access your voicemail messages here. Listen to recordings, see caller information, and manage your voicemail queue efficiently.',
      placement: 'bottom',
      onShow: () => {
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="communication-tab-voicemail"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
    {
      id: 'comm-filters',
      targetSelector: '[data-tour="communication-filters"]',
      title: 'Filter Communications',
      description:
        'Use filters to find specific conversations. Search by contact name or number, filter by direction (inbound/outbound), date range, or status.',
      placement: 'bottom',
      onShow: () => {
        // Switch back to calls tab to show filters
        setTimeout(() => {
          const tab = document.querySelector('[data-tour="communication-tab-calls"]') as HTMLElement;
          if (tab) {
            tab.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
            tab.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
            tab.click();
          }
        }, 50);
      },
    },
  ],
};
